import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    fetchCorporateLogos,
    addCorporateLogo,
    editCorporateLogo,
    updateCorporateLogo,
    trashCorporateLogo,
} from "../../Adapters/ApiAdapter";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseImage_path } from "../../variables/Variables";

const HomeCorporateLogos = () => {
    const [logos, setLogos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editItem, setEditItem] = useState(null);
    const [name, setName] = useState("");
    const [logoFile, setLogoFile] = useState(null);
    const [loadingAction, setLoadingAction] = useState(false);

    const loadLogos = async () => {
        setLoading(true);
        try {
            const res = await fetchCorporateLogos();
            setLogos(res.data || []);
        } catch {
            toast.error("Failed to load logos");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadLogos();
    }, []);

    const handleFileChange = (e) => setLogoFile(e.target.files[0]);

    const handleSubmit = async () => {
        if (!logoFile) return toast.error("Logo file is required");
        const formData = new FormData();
        if (name) formData.append("name", name);
        formData.append("logo", logoFile);
        setLoadingAction(true);
        try {
            await addCorporateLogo(formData);
            toast.success("Logo added successfully");
            setName("");
            setLogoFile(null);
            loadLogos();
        } catch {
            toast.error("Failed to add logo");
        } finally {
            setLoadingAction(false);
        }
    };

    const handleEdit = async (id) => {
        try {
            const res = await editCorporateLogo(id);
            setEditItem(res.data);
            setName(res.data.name || "");
            setLogoFile(null);
        } catch {
            toast.error("Failed to fetch data for editing");
        }
    };

    const handleUpdate = async () => {
        const formData = new FormData();
        if (name) formData.append("name", name);
        if (logoFile) formData.append("logo", logoFile);

        setLoadingAction(true);
        try {
            await updateCorporateLogo(editItem.id, formData);
            toast.success("Logo updated successfully");
            setEditItem(null);
            setName("");
            setLogoFile(null);
            loadLogos();
        } catch {
            toast.error("Failed to update logo");
        } finally {
            setLoadingAction(false);
        }
    };

    const handleTrash = async (id) => {
        try {
            await trashCorporateLogo(id);
            toast.success("Logo removed");
            loadLogos();
        } catch {
            toast.error("Failed to delete logo");
        }
    };

    const columns = [
        { field: "id", headerName: "ID", width: 80 },
        {
            field: "logo",
            headerName: "Logo",
            width: 120,
            renderCell: (params) =>
                params.row.logo ? (
                    <img
                        src={`${baseImage_path}/${params.row.logo}`}
                        alt="logo"
                        style={{ width: 60, height: 40, objectFit: "contain" }}
                    />
                ) : (
                    "No Logo"
                ),
        },
        {
            field: "name",
            headerName: "Name",
            flex: 1,
            renderCell: (params) => params.row.name || "N/A",
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 200,
            renderCell: (params) => (
                <Box>
                    <Button size="small" onClick={() => handleEdit(params.row.id)} variant="contained">
                        Edit
                    </Button>
                    <Button
                        size="small"
                        color="error"
                        onClick={() => handleTrash(params.row.id)}
                        variant="contained"
                        sx={{ ml: 1 }}
                    >
                        Trash
                    </Button>
                </Box>
            ),
        },
    ];

    return (
        <Box m={2}>
            <ToastContainer />
            <Typography variant="h4" gutterBottom>
                Corporate Logos
            </Typography>

            <Box display="flex" alignItems="center" gap={2} my={2}>
                <TextField
                    label="Name (optional)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
                <input type="file" accept="image/*" onChange={handleFileChange} />
                <Button variant="contained" onClick={handleSubmit} disabled={loadingAction}>
                    {loadingAction ? <CircularProgress size={20} /> : "Add"}
                </Button>
            </Box>

            <DataGrid
                rows={logos}
                columns={columns}
                getRowId={(row) => row.id}
                autoHeight
                loading={loading}
            />

            {/* Edit Dialog */}
            {editItem && (
                <Dialog open onClose={() => setEditItem(null)}>
                    <DialogTitle>Edit Corporate Logo</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Name"
                            fullWidth
                            margin="normal"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        <Box mt={2}>
                            <Typography variant="subtitle2">Current Logo:</Typography>
                            <img
                                src={`${baseImage_path}/${editItem.logo}`}
                                alt="Current"
                                style={{ width: 100, height: 60, objectFit: "contain" }}
                            />
                            <input type="file" onChange={handleFileChange} style={{ marginTop: 10 }} />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEditItem(null)}>Cancel</Button>
                        <Button variant="contained" onClick={handleUpdate} disabled={loadingAction}>
                            {loadingAction ? <CircularProgress size={20} /> : "Update"}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
};

export default HomeCorporateLogos;
